import React, { useEffect, useState, useRef } from 'react';
import { Layout, Menu, Image, Input, Button, Popover } from 'antd';
import type { MenuProps } from 'antd';
import {
  HomeOutlined,
  FolderOutlined,
  CreditCardOutlined,
  WalletOutlined,
  UserOutlined,
  TeamOutlined,
  FormOutlined,
  FileOutlined,
  LogoutOutlined,
  GiftOutlined,
  TagOutlined,
  DashboardOutlined,
  UserAddOutlined,
  TagsOutlined,
  ToolOutlined,
  DatabaseOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import logoImage from '../../assets/login/logo.png';
import { ROLE_TYPE, USER_STATUS } from '../../constants';
import { iGet } from '../../util/http';
import { UserInfo } from '../../types/api';

// 自定义样式
import './layout.css';

const { Header, Sider, Content } = Layout;

interface MainLayoutProps {
  children: React.ReactNode;
}

// Mock user data
const mockUsers = [
  { id: '1', name: '李婷', avatar: 'https://raw.githubusercontent.com/trevortylerlee/n1/main/n1.jpeg' },
  { id: '2', name: '李萌', avatar: 'https://raw.githubusercontent.com/trevortylerlee/n1/main/n1.jpeg' },
  { id: '3', name: '钱琳', avatar: 'https://raw.githubusercontent.com/trevortylerlee/n1/main/n1.jpeg' },
  { id: '4', name: '李新萍', avatar: 'https://raw.githubusercontent.com/trevortylerlee/n1/main/n1.jpeg' },
  { id: '5', name: '周永琪', avatar: 'https://raw.githubusercontent.com/trevortylerlee/n1/main/n1.jpeg' },
  { id: '6', name: '王民琦', avatar: 'https://raw.githubusercontent.com/trevortylerlee/n1/main/n1.jpeg' },
  { id: '7', name: '赵容', avatar: 'https://raw.githubusercontent.com/trevortylerlee/n1/main/n1.jpeg' },
  { id: '8', name: '吴萨霖', avatar: 'https://raw.githubusercontent.com/trevortylerlee/n1/main/n1.jpeg' },
  { id: '9', name: '何思其', avatar: 'https://raw.githubusercontent.com/trevortylerlee/n1/main/n1.jpeg' },
];

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchText, setSearchText] = React.useState('');
  const [filteredUsers, setFilteredUsers] = React.useState(mockUsers);
  const [userInfo, setUserInfo] = React.useState<UserInfo | null>(null);
  const [showPlannerPopover, setShowPlannerPopover] = useState(false);

  // Header 显示/隐藏逻辑
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);

  // Sider 滚动条逻辑
  const siderRef = useRef<HTMLDivElement>(null);
  const [siderScrollbarVisible, setSiderScrollbarVisible] = useState(false);


  // Check if hide parameter is set to true
  const hideUI = searchParams.get('hide') === 'true';

  const isCollegeAdmissionPage = location.pathname.replace(/^\//, '') === 'college-admission';

  const currentPath = location.pathname.replace(/^\//, '');
  const currentRootPath = currentPath.split('/')[0];

  const selectedKeys = React.useMemo(() => {
    if (currentPath.includes('/')) {
      return [currentPath];
    }
    return [currentRootPath || 'dashboard'];
  }, [currentPath, currentRootPath]);

  const openKeys = React.useMemo(() => {
    if (currentRootPath === 'online') return ['online'];
    if (currentRootPath === 'data-management') return ['data-management'];
    return [];
  }, [currentRootPath]);

  React.useEffect(() => {
    if (!currentPath) {
      navigate('/dashboard');
    }
  }, [currentPath, navigate]);

  // Header 滚动与鼠标事件处理
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current && currentScrollY > 64) { // 向下滚动且超过 Header 高度
        setHeaderVisible(false);
      } else { // 向上滚动
        setHeaderVisible(true);
      }
      lastScrollY.current = currentScrollY;
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (event.clientY < 50) {
        setHeaderVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Sider 滚动条事件处理
  useEffect(() => {
    const siderElement = siderRef.current;
    if (!siderElement) return;

    let timeoutId: NodeJS.Timeout;

    const handleSiderScroll = () => {
      setSiderScrollbarVisible(true);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setSiderScrollbarVisible(false);
      }, 1500); // 1.5秒后隐藏滚动条
    };

    siderElement.addEventListener('scroll', handleSiderScroll);
    return () => {
      siderElement.removeEventListener('scroll', handleSiderScroll);
      clearTimeout(timeoutId);
    };
  }, []);


  // 搜索处理函数
  const handleSearch = (value: string) => {
    setSearchText(value);
    setFilteredUsers(
      mockUsers.filter(user => 
        user.name.toLowerCase().includes(value.toLowerCase())
      )
    );
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'dashboard',
      icon: <HomeOutlined />,
      label: '首页'
    },
    // {
    //   key: 'college-admission',
    //   icon: <DatabaseOutlined />,
    //   label: '院校数据'
    // },
    {
      key: 'offline',
      icon: <FolderOutlined />,
      label: '线下建档'
    },
    userInfo?.user_info?.role_id === ROLE_TYPE.PLANNER ? {
      key: 'online',
      icon: <FileOutlined />,
      label: '线上接单',
      children: [
        {
          key: 'online/orders',
          label: '线上订单'
        },
        {
          key: 'online/services',
          label: '服务列表'
        },
        {
          key: 'online/consumer',
          label: '消费列表'
        }
      ]
    } : null,
    // {
    //   key: 'planner',
    //   icon: <FormOutlined />,
    //   label: '规划师表单'
    // },
    {
      key: 'card-management',
      icon: <CreditCardOutlined />,
      label: '卡码管理',
      children: [
        {
          key: 'card/member',
          label: '志愿会员卡'
        },
        {
          key: 'card/admission',
          label: '志愿升学卡'
        },
        {
          key: 'coupon',
          label: '优惠码'
        },
        {
          key: 'invite-code',
          label: '邀请码'
        }
      ]
    },
    {
      key: 'wallet-management',
      icon: <WalletOutlined />,
      label: '钱包管理',
      children: [
        {
          key: 'wallet',
          label: '现金收益'
        },
        {
          key: 'points',
          label: '积分详情'
        }
      ]
    },
    userInfo?.user_info?.role_id === ROLE_TYPE.ORG ? {
      key: 'team',
      icon: <TeamOutlined />,
      label: '团队管理'
    } : null,
    {
      key: 'basic',
      icon: <UserOutlined />,
      label: '基础信息'
    }
  ].filter(Boolean);

  // 退出登录
  const logout = () => {
    window.localStorage.removeItem('token')
    window.localStorage.removeItem('userInfo')
    navigate('/')
  }

  // 渲染侧边栏内容
  const renderSideContent = () => {
    if (isCollegeAdmissionPage) {
      // return (
      //   <div className="flex flex-col h-full bg-white/30 backdrop-blur-md rounded-xl shadow-lg p-4 overflow-y-auto scrollbar-none">
      //     <div className="relative mb-4">
      //       <Input
      //         placeholder="搜索服务人员"
      //         value={searchText}
      //         onChange={e => handleSearch(e.target.value)}
      //         suffix={<SearchOutlined className="text-gray-400" />}
      //         className="w-full rounded-lg border-gray-200 focus:border-indigo-500"
      //       />
      //     </div>
      //     <div className="flex flex-col gap-2">
      //       {filteredUsers.map(user => (
      //         <div
      //           key={user.id}
      //           className="flex items-center gap-3 p-3 rounded-lg cursor-pointer border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/30 transition-all duration-200"
      //         >
      //           <img
      //             src={user.avatar}
      //             alt={user.name}
      //             className="w-10 h-10 rounded-full object-cover"
      //           />
      //           <span className="text-gray-700 text-sm font-medium">{user.name}</span>
      //         </div>
      //       ))}
      //     </div>
      //   </div>
      // );
      return (
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          defaultSelectedKeys={['dashboard']}
          defaultOpenKeys={openKeys}
          style={{ height: '100%', borderRight: 0 }}
          onClick={({ key }) => navigate(`/${key}`)}
          items={menuItems}
          className="custom-menu bg-transparent"
        />
      );
    }

    return (
      <Menu
        mode="inline"
        selectedKeys={selectedKeys}
        defaultSelectedKeys={['dashboard']}
        defaultOpenKeys={openKeys}
        style={{ height: '100%', borderRight: 0 }}
        onClick={({ key }) => navigate(`/${key}`)}
        items={menuItems}
        className="custom-menu bg-transparent"
      />
    );
  };

  // 获取用户信息
  useEffect(() => {
    if(currentPath !== 'login' && !userInfo?.user_info) {
      iGet<UserInfo>('/backend/userInfo').then((response)=> {
        if (response?.code === 0 && response.data?.user_info) {
          localStorage.setItem('userInfo', JSON.stringify(response.data));
          setUserInfo(response.data);
        }
      })
    }
  }, [currentPath, userInfo?.user_info])

  // 获取用户角色显示文本
  const getUserRoleText = () => {
    if (!userInfo?.user_info?.role_id) {
      return '普通用户';
    }
    return userInfo?.user_info?.role_id === ROLE_TYPE.ORG ? '机构账号' : '个人规划师';
  };

  if(currentPath === 'login' || hideUI) {
    return <>{children}</>;
  }

  const showJoinButton = userInfo?.user_info?.status === USER_STATUS.NOT_SUBMITTED || userInfo?.user_info?.status === USER_STATUS.AUDIT_REJECTED
  
  // 用户信息弹窗内容
  const plannerPopoverContent = (
    <div style={{ width: '90vw', maxWidth: '1000px', height: '600px', maxHeight: '80vh', overflow: 'hidden' }}>
      <iframe src='/planner?hide=true' className='w-full' style={{ height: '600px', width: '100%', border: 'none' }}></iframe>
    </div>
  );
  
  return (
    <div className="bg-gradient-to-br from-indigo-10 via-purple-50 to-blue-100 min-h-screen">
      {/* 主容器 - 最大宽度1200px，水平居中 */}
      <div className="max-w-[1200px] mx-auto relative" style={{ minHeight: '100vh' }}>
        {/* 顶部导航栏 */}
        <header
          className={`fixed top-0 left-1/2 transform -translate-x-1/2 z-50 transition-transform duration-300 bg-transparent backdrop-blur-md w-full max-w-[1200px]`}
          style={{ transform: `translateX(-50%) ${headerVisible ? 'translateY(0)' : 'translateY(-100%)'}`, height: 64 }}
        >
          <div className="flex items-center h-16 px-4">
            {/* Logo与系统名 */}
            <div className="flex items-center cursor-pointer hover:-translate-y-0.5 transition-all duration-300" onClick={() => navigate('/dashboard')}>
              <div className="w-12 h-12 flex items-center justify-center mr-3 transition-all bg-indigo-500/10 rounded-lg">
                <UserOutlined className="text-indigo-600 text-2xl" />
              </div>
              <span className="font-extrabold text-xl text-indigo-900 hover:text-indigo-700 transition-all">中科团生涯规划管理系统</span>
            </div>
            
            {/* 右侧工具栏 */}
            <div className="flex-1 flex justify-end items-center space-x-6">
              {/* 入驻平台按钮 */}
              {showJoinButton && (
                <Button 
                  type='primary' 
                  className='rounded-full bg-indigo-600 hover:bg-indigo-700 border-none transition-all duration-300 hover:-translate-y-0.5 px-5 py-2 text-sm font-semibold'
                  onClick={() => navigate('/role')}
                >
                  入驻平台
                </Button>
              )}
              
              {/* 用户信息组件 */}
              <Popover 
                content={plannerPopoverContent}
                trigger="click"
                open={showPlannerPopover}
                onOpenChange={(visible) => setShowPlannerPopover(visible)}
                overlayStyle={{ maxWidth: '90vw' }}
                placement="bottomRight"
                overlayClassName="custom-popover"
              >
                <div 
                  className="flex flex-col items-start cursor-pointer px-3 py-1.5 rounded-lg hover:bg-indigo-100/30 transition-all duration-200"
                  onClick={() => setShowPlannerPopover(true)}
                >
                  <span className="text-gray-700 text-sm font-semibold">{userInfo?.user_info?.name || ' '}</span>
                  <span className="mt-1 text-indigo-700 text-xs font-semibold">{getUserRoleText()}</span>
                </div>
              </Popover>
              
              {/* 退出按钮 */}
              <a 
                href="#" 
                className="text-indigo-700 hover:text-indigo-800 transition-colors duration-200 flex items-center px-2 py-1 hover:bg-indigo-100/30 rounded-lg"
                onClick={logout}
              >
                <LogoutOutlined className='mr-1' />
                退出
              </a>
            </div>
          </div>
        </header>
        
        {/* 主体区域：侧边栏+内容区 */}
        <div className="flex w-full" style={{ paddingTop: 64 }}>
          {/* 侧边栏 */}
          <aside
            className={`z-40 transition-all duration-300 pt-4 w-48 bg-transparent backdrop-blur-md`}
            ref={siderRef}
          >
            <div className={`h-full overflow-y-auto ${siderScrollbarVisible ? 'show-scrollbar' : 'scrollbar-none'}`}>
              {renderSideContent()}
            </div>
          </aside>

          {/* 主内容区域 */}
          <main className="flex-1 pl-6">
            <div className="w-full bg-transparent backdrop-blur-md rounded-xl min-w-[980px]">
              <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                {children}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default MainLayout; 






